import { ValueObject } from '@nx-smartmonkey/shared/domain';
import { removeEmpty } from '@nx-smartmonkey/shared/helpers';

export type CouponDurationTime = `repeating` | `forever` | `once`;

interface UpdateBillingInfoProps {
  name?: string | null;
  city?: string | null;
  country?: string | null;
  line1?: string | null;
  line2?: string | null;
  postal_code?: string | null;
  state?: string | null;
  tax_value?: string | null;
  tax_id?: string | null; // "eu_vat", "mx_rfc"...
}

export interface SubscriptionCoupon {
  id: string;
  amount_off: number;
  percent_off: number;
  duration: CouponDurationTime;
  duration_in_months: number;
  valid: boolean;
}

export type BillingPeriod = `year` | `month`;

export type PlanType = `monkey` | `gorilla` | `kong` | `drivers:free` | `drivers:lite`;

export type StatusType =
  | `active`
  | `incomplete`
  | `incomplete_expired`
  | `trialing`
  | `past_due`
  | `canceled`
  | `unpaid`;

export interface SubscriptionSMS {
  enabled: boolean;
  credit: number;
}
export interface SubscriptionWhatsApp {
  enabled: boolean;
  credit: number;
}

export interface SubscriptionEmails {
  period_usage: number;
  extended_limit: number;
}

export interface SubscriptionBillingInfo {
  name: string;
  city: string;
  country: string;
  line1: string;
  line2?: string;
  postal_code?: string;
  state?: string;
  tax_value?: string;
  tax_id?: string; // "eu_vat", "mx_rfc"...
}

export interface SubscriptionCreditCard {
  stripe_id: string;
  brand?: string;
  fingerprint: string;
  funding: string;
  name?: string;
  address_line1?: string;
  address_line2?: string;
  address_city?: string;
  address_country?: string;
  address_state?: string;
  address_zip?: string;
  last4?: string;
  exp_month?: number;
  exp_year?: number;
}

export interface SubscriptionProps {
  updated_at: Date;

  plan: PlanType;
  billing_period: BillingPeriod;
  vehicles: number;

  current_period_end: Date;
  scheduled_update: boolean;
  will_cancel?: boolean;

  status: StatusType;
  tax_percent: number;
  card?: SubscriptionCreditCard;
  coupon?: string;
  billing_info?: SubscriptionBillingInfo;
  client_secret: string;

  emails: SubscriptionEmails;
  sms: SubscriptionSMS;
  whatsapp: SubscriptionWhatsApp;

  geocode_credit: number;

  has_contract: boolean;

  users: number;
  projects: number;
  supervisors: number;
  traffic: boolean;

  extra_vehicles: number;
  extra_users: number;
  extra_projects: number;
  extra_supervisors: number;
}

export class Subscription extends ValueObject<SubscriptionProps> {
  get updated_at(): Date {
    return this.props.updated_at;
  }

  get plan(): PlanType {
    return this.props.plan;
  }

  get billing_period(): BillingPeriod {
    return this.props.billing_period;
  }

  get vehicles(): number {
    return this.props.vehicles;
  }

  get current_period_end(): Date {
    return this.props.current_period_end;
  }

  get scheduled_update(): boolean {
    return this.props.scheduled_update;
  }

  get will_cancel(): boolean | undefined {
    return this.props.will_cancel;
  }

  get status(): StatusType {
    return this.props.status;
  }

  get tax_percent(): number {
    return this.props.tax_percent;
  }

  get card(): SubscriptionCreditCard | undefined {
    return this.props.card;
  }

  get coupon(): string | undefined {
    return this.props.coupon;
  }

  get billing_info(): SubscriptionBillingInfo | undefined {
    return this.props.billing_info;
  }

  set billing_info(billingInfo: SubscriptionBillingInfo | undefined) {
    this.props.billing_info = billingInfo;
  }

  get client_secret(): string {
    return this.props.client_secret;
  }

  get emails(): SubscriptionEmails {
    return this.props.emails;
  }

  get sms(): SubscriptionSMS {
    return this.props.sms;
  }

  get whatsapp(): SubscriptionWhatsApp {
    return this.props.whatsapp;
  }

  get geocode_credit(): number {
    return this.props.geocode_credit;
  }

  get users(): number {
    return this.props.users;
  }

  get projects(): number {
    return this.props.projects;
  }

  get supervisors(): number {
    return this.props.supervisors;
  }

  get traffic(): boolean {
    return this.props.traffic;
  }

  get extra_vehicles(): number {
    return this.props.extra_vehicles;
  }

  get extra_users(): number {
    return this.props.extra_users;
  }

  get extra_projects(): number {
    return this.props.extra_projects;
  }

  get extra_supervisors(): number {
    return this.props.extra_supervisors;
  }

  get has_contract(): boolean {
    return this.props.has_contract;
  }

  static create(props: SubscriptionProps): Subscription {
    return new Subscription(props);
  }

  updateBillingInfo(props: UpdateBillingInfoProps) {
    removeEmpty(props);
    if (!this.billing_info) {
      this.billing_info = {} as any;
    }
    if (props.name) this.billing_info!.name = props.name;
    if (props.city) this.billing_info!.city = props.city;
    if (props.country) this.billing_info!.country = props.country;
    if (props.line1) this.billing_info!.line1 = props.line1;
    if (props.line2) this.billing_info!.line2 = props.line2;
    if (props.postal_code) this.billing_info!.postal_code = props.postal_code;
    if (props.state) this.billing_info!.state = props.state;
    if (props.tax_value) this.billing_info!.tax_value = props.tax_value;
    if (props.tax_id) this.billing_info!.tax_id = props.tax_id;
  }

  getSubscriptionMaxCustomerEmails(): number {
    const CUSTOMER_EMAILS_MULTIPLIER = 1000;
    return this.vehicles * CUSTOMER_EMAILS_MULTIPLIER + (this.emails?.extended_limit ?? 0);
  }

  isCanceled(): boolean {
    return (
      !this.isSoloDrivers() &&
      (new Date(this.current_period_end) < new Date() ||
        this.status === `canceled` ||
        this.status === `incomplete_expired`)
    );
  }

  isSoloDrivers(): boolean {
    return this.plan === `drivers:free` || this.plan === `drivers:lite`;
  }
}
